// MapMeshesAdapter
//
// === SINGLE-ACTIVE-INSTANCE INVARIANT (overlap fix) ===
//
// Background: the octree exporter routes an instance into every leaf
// its AABB straddles. A wall, rebar, or beam near a tile boundary lives
// in 2-6 tiles. This is CORRECT for streaming — each tile-membership
// keeps the element renderable when its host tile is the one in view.
// Removing those memberships (write-time filter) would mean: pan to a
// neighbor tile, the wall on the boundary disappears even though it's
// in view. The membership IS the frustum-culling unit.
//
// But materializing every membership produces visual duplication: same
// element drawn in N InstancedMeshes at the same world position.
// Z-fighting, shimmering, inflated draw counts, and worse: tree
// interactions resolve via `viewer.meshesObject.get(uid)` which is
// keyed by uniqueId — exactly ONE record wins. Click highlight colors
// 1 of N slots. Hide collapses 1 of N to zero scale. The other N-1
// stay visible, rendering normally, ignoring all interactions.
//
// This adapter enforces the invariant: ONE slot per uid is RENDERED at
// any time, the rest are matrix-collapsed to invisible (zero scale).
// First-tile-wins. When a tile unmaterializes, its actively-rendered
// uids are handed off ("promoted") to a slot in another currently-
// active tile that owns the same uid, if one exists.
//
// === STATE-STORE INTEGRATION ===
//
// Promotion has a subtle bug pre-fix: when promoting an inactive slot
// to active, the adapter blindly restores its snapshot matrix. If the
// user had hidden the uid (via the state store), the promoted slot
// would silently un-hide it. Fix: when promoting, route through the
// applicator instead of restoring the snapshot directly. The applicator
// reads store state (hidden/isolated/highlight) and writes the correct
// effective matrix + color.
//
// Same logic applies on initial register: if a hidden uid first appears
// in this session via a NEW tile (not a re-show), the active slot needs
// to reflect "hidden" right away. The applicator runs at materialize
// time (in InstanceMaterializer) so the matrix is already correct
// before adapter.register sees the slots — we don't need a second pass
// here. But we DO need the applicator handle so promotion works.
//
// === DATA SHAPE ===
//
// activeByTile: Map<tileKey, {
//   fragments: InstancedMesh[],
//   slotsByUid: Map<uid, [{ mesh, index, originalMatrix }, ...]>,
// }>
//
// activeUidOwner: Map<uid, { tileKey, record }>

import { Matrix4 } from 'three';
import { markSceneChanged } from './renderSignal';

export default class MapMeshesAdapter {
  constructor(viewer, { applicator = null, store = null } = {}) {
    this.viewer = viewer;
    if (!viewer.meshesObject) viewer.meshesObject = new Map();
    if (!viewer.meshes) viewer.meshes = [];

    // Optional dependencies — if either is null, adapter behaves like
    // it always did (snapshot-restore on promote). Store/applicator
    // are wired in by StreamingLoader at construction time when
    // streaming mode is active.
    this.applicator = applicator;
    this.store = store;

    // tileKey -> { fragments, slotsByUid }
    this.activeByTile = new Map();

    // uid -> { tileKey, record }
    this.activeUidOwner = new Map();

    // uid -> Array<{ tileKey, slotIndex }>
    //
    // Reverse index for O(slotsForUid) state application. Without this,
    // _applyForUidAcrossTiles must walk every active tile per uid and
    // do a second O(tiles) reverse lookup to identify the active tile.
    // For large selections in many-tile scenes this is the dominant
    // hide/show cost. Maintained by register/unregister/_promoteSuccessor.
    this.activeSlotsByUid = new Map();

    // mesh -> Set of every object we hold that points at it (slot
    // snapshots, owner records and their fragment entries), and
    // mesh -> Set of tile entries whose fragments[] list it. A pool grow
    // swaps its InstancedMesh; _refreshMeshRef rewires exactly these
    // instead of walking every slot of every tile (see there).
    this._holdersByMesh = new Map();
    this._entriesByMesh = new Map();
    this._refreshVisits = 0;

    // Reusable temp matrices to avoid per-call allocation in the
    // hot show/hide path.
    this._tmpMatrix = new Matrix4();
    this._zeroMatrix = new Matrix4().makeScale(0.0001, 0.0001, 0.0001);
  }

  register(tileKey, mergedRecords, mergedFragments) {
    if (this.activeByTile.has(tileKey)) return;

    const slotsByUid = new Map();

    mergedRecords.forEach((rec, uid) => {
      // A record's `fragments` is the array the materializer built —
      // one entry per slot this tile owns for the uid. Snapshot every
      // slot's matrix so promotion/restore later can copy it back
      // verbatim. (For disabled instances the snapshot is zero-scale,
      // which is correct — show-after-promote should keep them
      // disabled.)
      const slots = rec.fragments.map(f => ({
        mesh: f.mesh,
        index: f.index,
        originalMatrix: this._snapshotMatrix(f.mesh, f.index),
      }));
      slotsByUid.set(uid, slots);
      for (const sl of slots) this._track(sl);

      // Mirror in the reverse index so getActiveSlotsForUid can return
      // the same set without walking activeByTile.
      let uidEntries = this.activeSlotsByUid.get(uid);
      if (!uidEntries) {
        uidEntries = [];
        this.activeSlotsByUid.set(uid, uidEntries);
      }
      for (let i = 0; i < slots.length; i++) {
        uidEntries.push({ tileKey, slotIndex: i });
      }

      const owner = this.activeUidOwner.get(uid);
      if (!owner) {
        // First tile to claim this uid. Make it the active renderer
        // and register the record in viewer.meshesObject so tree-click
        // resolves to this tile's slot.
        //
        // The slot's matrix was already written correctly at
        // materialize time (by the applicator if present, or default
        // delta if not). No additional work needed here.
        this.activeUidOwner.set(uid, { tileKey, record: rec });
        this.viewer.meshesObject.set(uid, rec);
        this._trackRecord(rec);
      } else {
        // Already active in another tile. Hide every slot we own for
        // this uid so we don't render the duplicate. On promotion
        // later (if the active tile unloads), the applicator decides
        // what state to write — store-aware, not blind-restore.
        slots.forEach(s => this._hideSlot(s));
      }
    });

    // viewer.meshes is the raycast set. With per-(hash) consolidated
    // pools, the SAME InstancedMesh shows up in many tiles' fragments
    // arrays — push-unconditionally would cause duplicate raycast hits
    // (the same instance reported twice). The pool registry's
    // onPoolCreated callback adds each pool mesh to viewer.meshes
    // exactly once at creation, so adapter.register is now a no-op
    // for viewer.meshes maintenance. We keep this branch for legacy
    // non-pooled meshes (every f without a pool gets pushed).
    mergedFragments.forEach(f => {
      if (f.userData && f.userData.hash && this.viewer.meshes.includes(f)) return;
      // Untracked mesh (legacy path or unusual case): push it once.
      if (!this.viewer.meshes.includes(f)) this.viewer.meshes.push(f);
    });
    const entry = { fragments: mergedFragments, slotsByUid };
    if (Array.isArray(mergedFragments)) {
      for (const f of mergedFragments) {
        if (!f) continue;
        let set = this._entriesByMesh.get(f);
        if (!set) { set = new Set(); this._entriesByMesh.set(f, set); }
        set.add(entry);
      }
    }
    this.activeByTile.set(tileKey, entry);
  }

  // ----- mesh-reference index (see _refreshMeshRef) -----
  _track(obj) {
    const m = obj && obj.mesh;
    if (!m) return;
    let set = this._holdersByMesh.get(m);
    if (!set) { set = new Set(); this._holdersByMesh.set(m, set); }
    set.add(obj);
  }

  _untrack(obj) {
    const m = obj && obj.mesh;
    if (!m) return;
    const set = this._holdersByMesh.get(m);
    if (!set) return;
    set.delete(obj);
    if (set.size === 0) this._holdersByMesh.delete(m);
  }

  _trackRecord(rec) {
    if (!rec) return;
    this._track(rec);
    if (Array.isArray(rec.fragments)) for (const f of rec.fragments) this._track(f);
  }

  _untrackRecord(rec) {
    if (!rec) return;
    this._untrack(rec);
    if (Array.isArray(rec.fragments)) for (const f of rec.fragments) this._untrack(f);
  }

  unregister(tileKey) {
    const entry = this.activeByTile.get(tileKey);
    if (!entry) return [];

    entry.slotsByUid.forEach((slots, uid) => {
      for (const sl of slots) this._untrack(sl);
      // Remove this tile's contributions from the reverse index by
      // replacing the array with a filtered copy. If the resulting
      // array is empty, drop the map entry entirely so a later lookup
      // returns undefined.
      const uidEntries = this.activeSlotsByUid.get(uid);
      if (uidEntries) {
        const filtered = uidEntries.filter(e => e.tileKey !== tileKey);
        if (filtered.length === 0) this.activeSlotsByUid.delete(uid);
        else this.activeSlotsByUid.set(uid, filtered);
      }

      const owner = this.activeUidOwner.get(uid);
      if (owner && owner.tileKey === tileKey) {
        this._untrackRecord(owner.record);
        this.activeUidOwner.delete(uid);
        this.viewer.meshesObject.delete(uid);
        this._promoteSuccessor(uid, tileKey);
      }
    });

    // Don't strip from viewer.meshes — pool meshes are shared across
    // tiles, so removing one because THIS tile unloaded would
    // accidentally yank it out from under every other tile still
    // using the same pool. Pool meshes are managed by the registry's
    // lifecycle; they stay in viewer.meshes until the pool itself is
    // disposed (which doesn't happen during normal tile unload).
    if (Array.isArray(entry.fragments)) {
      for (const f of entry.fragments) {
        const set = f && this._entriesByMesh.get(f);
        if (!set) continue;
        set.delete(entry);
        if (set.size === 0) this._entriesByMesh.delete(f);
      }
    }
    this.activeByTile.delete(tileKey);
    return entry.fragments;
  }

  has(tileKey) { return this.activeByTile.has(tileKey); }

  // Returns the reverse-index entries for a uid, or undefined if the
  // uid is not currently registered in any active tile. Callers
  // (notably InstanceStateApplicator) iterate these instead of walking
  // every active tile per uid.
  getActiveSlotsForUid(uid) {
    return this.activeSlotsByUid.get(uid);
  }

  // ----- internals -----

  // O(tiles holding this uid), not O(all tiles): the candidates come from
  // the reverse index (activeSlotsByUid, already filtered of the tile being
  // unregistered). It walked every active tile per promoted uid, so a
  // forced swap of a tile with a few hundred owned uids cost
  // uids x 1,469 tiles - Tom, real GPU round 6: one 3.25 s tick, 1.9 s of it
  // _promoteSuccessor <- unregister <- _applyLodSwap.
  _promoteSuccessor(uid, excludeTileKey) {
    const entries = this.activeSlotsByUid.get(uid);
    const seenTiles = new Set();
    for (const e of (entries || [])) {
      const otherTileKey = e.tileKey;
      if (otherTileKey === excludeTileKey || seenTiles.has(otherTileKey)) continue;
      seenTiles.add(otherTileKey);
      const otherEntry = this.activeByTile.get(otherTileKey);
      if (!otherEntry) continue;
      const slots = otherEntry.slotsByUid.get(uid);
      if (!slots || slots.length === 0) continue;
      this._promoteScans = (this._promoteScans || 0) + 1;

      // Found a candidate. Re-register meshesObject FIRST so the
      // applicator's _applyForUidAcrossTiles correctly identifies
      // this tile as the active owner.
      const first = slots[0];
      const newRec = {
        mesh: first.mesh,
        uniqueId: uid,
        id: uid,
        index: first.index,
        fragments: slots.map(s => ({ mesh: s.mesh, index: s.index })),
      };
      this.activeUidOwner.set(uid, { tileKey: otherTileKey, record: newRec });
      this.viewer.meshesObject.set(uid, newRec);
      this._trackRecord(newRec);

      if (this.applicator) {
        // Route through applicator so store state (hide/isolate/
        // highlight) gets written to the slot. The applicator handles
        // the visible-vs-hidden composition, including writing
        // zero-scale if the uid is currently hidden — which is the
        // exact scenario we're protecting against.
        this.applicator.applyForUids(this, [uid]);
      } else {
        // No store/applicator wired — fallback to legacy
        // snapshot-restore behavior. (Used when streaming mode is off
        // or when adapter is constructed without dependencies for
        // backwards-compat.)
        slots.forEach(s => this._showSlot(s));
      }
      return;
    }
    // No successor — uid is now inactive. meshesObject already cleared
    // by caller; nothing else to do.
  }

  _snapshotMatrix(mesh, index) {
    const m = new Matrix4();
    mesh.getMatrixAt(index, m);
    return m;
  }

  _hideSlot(slot) {
    slot.mesh.setMatrixAt(slot.index, this._zeroMatrix);
    slot.mesh.instanceMatrix.needsUpdate = true;
    markSceneChanged('adapterHide');
  }

  _showSlot(slot) {
    slot.mesh.setMatrixAt(slot.index, slot.originalMatrix);
    slot.mesh.instanceMatrix.needsUpdate = true;
    markSceneChanged('adapterShow');
  }

  // Called by the streaming loader's `onMeshSwapped` callback when a
  // GlobalInstancePool grows and replaces its underlying InstancedMesh.
  // Walks every place we stored a direct mesh reference and swaps
  // `oldMesh` → `newMesh`. Without this, every slot/record built
  // BEFORE the grow points at a disposed mesh — setMatrixAt would
  // write to dead GPU memory, hide/show/select silently no-op for
  // those tiles, and raycast may still hit the old mesh (which is
  // also fixed in the loader's same callback for viewer.meshes).
  //
  // The three reference sites are:
  //
  //   1. `activeByTile[*].fragments[]`    — per-tile cached fragment
  //                                         list (returned by unregister).
  //   2. `activeByTile[*].slotsByUid[*]`  — per-uid slot snapshots used
  //                                         by _hideSlot / _showSlot.
  //   3. `activeUidOwner[*].record`       — same object stored in
  //                                         `viewer.meshesObject`, so
  //                                         updating record.mesh here
  //                                         fixes both at once.
  //
  // The pool-grow path is intended to be cold (pre-sizing in the
  // registry covers the common case), so this is O(active tiles ×
  // slots) on a rare event — fine.
  //
  // IT IS NOT COLD. Pools start at 16-256 slots and double, so on BA03
  // (4,600 pools, 884k slots) grows happen thousands of times in a load,
  // and the walk over every slot of every tile, every owner record and -
  // in the loader - every record in meshesObject was ~10 s of main thread
  // (real-GPU trace 2026-10-13, source-mapped: _refreshMeshRef 4.2 + 2.4 s,
  // onMeshSwapped 3.2 s). Now it visits only what points at oldMesh,
  // through the index register/unregister/_promoteSuccessor keep.
  _refreshMeshRef(oldMesh, newMesh) {
    const entries = this._entriesByMesh.get(oldMesh);
    if (entries) {
      this._entriesByMesh.delete(oldMesh);
      let into = this._entriesByMesh.get(newMesh);
      if (!into) { into = new Set(); this._entriesByMesh.set(newMesh, into); }
      entries.forEach((entry) => {
        if (Array.isArray(entry.fragments)) {
          for (let i = 0; i < entry.fragments.length; i++) {
            this._refreshVisits++;
            if (entry.fragments[i] === oldMesh) entry.fragments[i] = newMesh;
          }
        }
        into.add(entry);
      });
    }
    const holders = this._holdersByMesh.get(oldMesh);
    if (holders) {
      this._holdersByMesh.delete(oldMesh);
      let into = this._holdersByMesh.get(newMesh);
      if (!into) { into = new Set(); this._holdersByMesh.set(newMesh, into); }
      holders.forEach((obj) => {
        this._refreshVisits++;
        if (obj.mesh === oldMesh) { obj.mesh = newMesh; into.add(obj); }
      });
    }
  }

  // ----- diagnostics -----

  stats() {
    let totalSlots = 0;
    const activeUids = this.activeUidOwner.size;
    const tiles = this.activeByTile.size;
    this.activeByTile.forEach(entry => {
      entry.slotsByUid.forEach(slots => { totalSlots += slots.length; });
    });
    return {
      tiles,
      activeUids,
      totalSlots,
      hiddenSlots: totalSlots - activeUids,
    };
  }
}